import { Button, Flex, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import type { ContractOverviewDto, ContractReportQueryDto } from "../../autogen/bff-api";
import { base64Encode } from "../../common/utils/base64";
import { formatNumber } from "../../common/formatting/formatNumber";
import { registerLink } from "./contractFigures";

export const MISSING_DATA_COLUMNS = ["title", "counterparty", "endDate", "pricePerYear", "pricePerMonth", "responsible"];

export const MissingContractData = ({ overview, base }: { overview: ContractOverviewDto; base: ContractReportQueryDto }) => {
  const { t } = useTranslation();
  const missing = overview.missingData;
  const frameworkScope = !!base.activeFrameworkAgreement;
  const withoutActiveOwner = overview.figures.find((figure) => figure.figure === "WITHOUT_ACTIVE_OWNER")?.value ?? 0;
  const link = `${registerLink(base, missing)}&columns=${base64Encode(JSON.stringify(MISSING_DATA_COLUMNS))}`;
  return (
    <Stack spacing="2" borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="4" bg="smBackground">
      <Flex justify="space-between" align="baseline" gap="3" wrap="wrap">
        <Text as="h2" fontWeight="semibold" color="smText">
          {t("Missing end date, value or responsible person")}
        </Text>
        {missing.contracts > 0 ? (
          <Button as={RouterLink} to={link} size="sm" variant="link" colorScheme="teal" data-testid="missing-data-link">
            {frameworkScope
              ? t("{{count}} suppliers in active framework agreements", { count: missing.contracts })
              : t("{{count}} contracts", { count: missing.contracts })}
          </Button>
        ) : (
          <Text fontSize="sm" color="smMuted">
            {t("None")}
          </Text>
        )}
      </Flex>
      <Text fontSize="sm" color="smMuted">
        {frameworkScope
          ? t("These suppliers in active framework agreements lack an end date, a value or a responsible person.")
          : t("These contracts lack an end date, a value or a responsible person.")}
      </Text>
      {missing.contracts > 0 && (
        <Text fontSize="sm" color="smMuted">
          {t("No end date: {{endDate}} · No value: {{value}} · No active responsible: {{owner}}", {
            endDate: formatNumber(overview.activeWithoutEndDate),
            value: formatNumber(overview.activeWithoutAnnualValue),
            owner: formatNumber(withoutActiveOwner),
          })}
        </Text>
      )}
    </Stack>
  );
};
